import { createApp } from 'vue'
import App from './App.vue'
import { router } from './router'
import { createHead } from '@unhead/vue/client'
import { trackSessionStart } from '@/utils/gtagEvent'
// SSE service
import { startSse } from './services/sseClient'
// Sound system
import { initializeSoundFiles, resumeAudio } from './utils/soundPlayer'
// Toast notifications
import Vue3Toastify, { type ToastContainerOptions } from 'vue3-toastify'
// Gtag
import { createGtag } from 'vue-gtag'
import { fireTwitterPageView } from '@/utils/twitterPixel'
import { initMetaPixel, trackMetaPageView } from '@/utils/metaPixel'

// i18n
import { i18n, loadLocale } from '@/i18n'

// State management
import { createPinia } from 'pinia'
import { useFeatureFlagsStore } from '@/store/featureFlags'
import { FeatureFlag } from '@/types/featureFlags'

// GSAP
import { gsap } from 'gsap'

// Global styles
import '@/styles/globals.scss'
import 'flag-icons/css/flag-icons.min.css'
import 'vue3-toastify/dist/index.css'

const gtag = createGtag({
  tagId: 'G-62KF2E4MLH',
})

const app = createApp(App)
const head = createHead()
const pinia = createPinia()

app.use(pinia)
app.use(head)
app.use(gtag)
app.use(Vue3Toastify, {
  autoClose: 3000,
  position: 'bottom-right',
  closeOnClick: true,
  pauseOnFocusLoss: false,
  hideProgressBar: true,
  theme: 'colored',
} as ToastContainerOptions)
app.use(i18n)

// Make GSAP globally available
app.config.globalProperties.$gsap = gsap

// Initialize Unleash feature flags
const featureFlagsStore = useFeatureFlagsStore(pinia)

// Load initial locale before mounting to avoid showing translation keys
loadLocale(i18n.global.locale.value as any).then(async () => {
  // Initialize feature flags before setting up router and mounting
  const flagsInitialized = await featureFlagsStore.initialize().catch((error) => {
    console.error('Failed to initialize feature flags:', error)
    return false
  })
  
  // On Unleash failure, enable all features by default (fail-open)
  if (!flagsInitialized) {
    console.warn('Feature flags unavailable - enabling all features by default')
    // Set all flags to true as fallback
    featureFlagsStore.flags = {
      [FeatureFlag.GAME_CASES]: true,
      [FeatureFlag.GAME_CASES_DAILYFREE]: true,
      [FeatureFlag.GAME_ARCADE_WORDFRAG]: true,
      [FeatureFlag.GAME_GIVEAWAYS]: true,
      [FeatureFlag.GAME_EVENTS]: true,
      [FeatureFlag.GAME_UPGRADE]: true,
      [FeatureFlag.GAME_ARENA]: true,
      [FeatureFlag.GAME_ARCADE_PRICE_DUEL]: true,
      [FeatureFlag.DEPOSIT_CRYPTO]: true,
      [FeatureFlag.DEPOSIT_GIFTCARD]: true,
      [FeatureFlag.DEPOSIT_SKINS]: true,
      [FeatureFlag.DEPOSIT_TOKENCODE]: true,
      [FeatureFlag.DEPOSIT_PAYOP]: true,
    }
  }
  
  // Check if any accessible game feature is enabled
  const accessibleGameFlags = [
    FeatureFlag.GAME_CASES,
    FeatureFlag.GAME_CASES_DAILYFREE,
    FeatureFlag.GAME_ARCADE_WORDFRAG,
    FeatureFlag.GAME_ARCADE_PRICE_DUEL,
    FeatureFlag.GAME_GIVEAWAYS,
    FeatureFlag.GAME_EVENTS,
    FeatureFlag.GAME_UPGRADE,
    FeatureFlag.GAME_ARENA,
  ]
  const hasAnyAccessibleGame = accessibleGameFlags.some(flag => 
    featureFlagsStore.isEnabled(flag)
  )
  
  if (!hasAnyAccessibleGame) {
    console.error('All accessible game features are disabled - redirecting to maintenance')
    window.location.href = '/maintenance.html'
    return
  }
  
  // Install router after feature flags are loaded
  app.use(router)
  initMetaPixel()

  router.afterEach((to) => {
    trackMetaPageView({
      path: to.fullPath,
      route_name: String(to.name ?? ''),
    })
  })
  
  // Set HTML lang attribute to match initial locale
  document.documentElement.lang = i18n.global.locale.value

  app.mount('#app')

  // Hide the loader with fade-out animation
  const loader = document.getElementById('app-loader')
  if (loader) {
    loader.classList.add('fade-out')
    // Remove from DOM after animation completes
    setTimeout(() => loader.remove(), 300)
  }

  // Initialize sound system on first user interaction (required for mobile)
  // iOS requires AudioContext to be created from within a user gesture
  let soundInitialized = false
  const initSoundOnInteraction = async () => {
    if (soundInitialized) return
    soundInitialized = true

    try {
      await initializeSoundFiles()
      await resumeAudio()
    } catch (error) {
      // Silently fail - audio is not critical
    }

    // Clean up listeners after first interaction
    document.removeEventListener('click', initSoundOnInteraction)
    document.removeEventListener('touchstart', initSoundOnInteraction)
    document.removeEventListener('pointerdown', initSoundOnInteraction)
  }

  document.addEventListener('click', initSoundOnInteraction, { once: true })
  document.addEventListener('touchstart', initSoundOnInteraction, { once: true })
  document.addEventListener('pointerdown', initSoundOnInteraction, { once: true })

  startSse()

  trackSessionStart()
  fireTwitterPageView()
})
